import type { ColumnDef } from '@tanstack/vue-table';
import type { Component, ComputedRef, MaybeRefOrGetter } from 'vue';
import type { SortDirection } from '#layers/base/public';
import type { AnyItem, CollectionFieldPath, GlobalPrimitiveFieldId, PrimitiveType, ResolvedMonospaceCollection } from '#layers/data-model/public';
import type { ColumnHeaderActionGroup } from '#layers/ui/public';
import { createColumnHelper } from '@tanstack/vue-table';
import { computed, h, toValue } from 'vue';
import { createDisplayTemplateRenderPlan, fieldPathToDisplayTemplateAst, getCollectionFieldPathLabels, resolveCollectionFieldPath, toCollectionFieldPathKey, useDataModelDisplayNames } from '#layers/data-model/public';
import LayoutTableFieldPathLabel from './LayoutTableFieldPathLabel.vue';
import { resolveCellPrimitiveValue } from './resolve-cell-primitive-value';

export function useTableColumns<T extends AnyItem>(options: {
	collection: MaybeRefOrGetter<ResolvedMonospaceCollection>;
	visibleColumnPaths: MaybeRefOrGetter<CollectionFieldPath[]>;
	renderDisplayTemplate: MaybeRefOrGetter<Component>;
	getHeaderActions?: (fieldPath: CollectionFieldPath) => ColumnHeaderActionGroup[] | undefined;
	onCellContextMenu?: (fieldPath: CollectionFieldPath, value: unknown, type: PrimitiveType, e: MouseEvent) => void;
	/** The directions a top-level field column may sort by. */
	sortDirections?: (fieldId: GlobalPrimitiveFieldId) => Array<SortDirection>;
}): { fieldColumns: ComputedRef<ColumnDef<T, unknown>[]> } {
	const columnHelper = createColumnHelper<T>();

	const { getFieldDisplayName } = useDataModelDisplayNames();

	// Keep TanStack descriptor identity stable across column edits.
	const fieldColumnCache = new WeakMap<object, Map<string, ColumnDef<T, unknown>>>();

	function getCachedFieldColumn(fieldPath: CollectionFieldPath): ColumnDef<T, unknown> | null {
		const collection = toValue(options.collection);
		let perCollection = fieldColumnCache.get(collection);

		if (!perCollection) {
			perCollection = new Map<string, ColumnDef<T, unknown>>();
			fieldColumnCache.set(collection, perCollection);
		}

		const columnId = toCollectionFieldPathKey(fieldPath);
		const sortDirections = fieldPath.length === 1 ? options.sortDirections?.(fieldPath[0] as GlobalPrimitiveFieldId) : undefined;
		// Allowed directions follow schema and permission refreshes, so they key the cached descriptor.
		const cacheKey = sortDirections ? `${columnId}|${sortDirections.join(',')}` : columnId;
		const cached = perCollection.get(cacheKey);

		if (cached) return cached;

		const segments = resolveCollectionFieldPath(collection, fieldPath);
		const leafSegment = segments?.at(-1);

		if (!segments || !leafSegment) return null;

		const primitiveLeafType = 'type' in leafSegment.field ? leafSegment.field.type : undefined;
		const isSortablePrimitiveColumn = fieldPath.length === 1 && primitiveLeafType !== undefined && sortDirections?.length !== 0;
		const wireCellContextMenu = Boolean(options.onCellContextMenu) && primitiveLeafType !== undefined;

		const renderPlan = createDisplayTemplateRenderPlan(fieldPathToDisplayTemplateAst(fieldPath), collection);

		const headerActions = options.getHeaderActions
			? () => options.getHeaderActions?.(fieldPath) ?? []
			: undefined;

		const descriptor = columnHelper.display({
			id: columnId,
			header: () =>
				h(LayoutTableFieldPathLabel, {
					labels: getCollectionFieldPathLabels(collection, fieldPath, getFieldDisplayName),
				}),
			enableSorting: isSortablePrimitiveColumn,
			enableHiding: true,
			enableOrdering: true,
			enablePinning: true,
			cell: ({ row }) =>
				h(toValue(options.renderDisplayTemplate), {
					item: row.original,
					renderPlan,
					size: 'sm',
					// A tooltip per cell multiplies the table's mount cost, and column resize already reveals the text.
					truncationTooltip: false,
				}),
			meta: (headerActions || wireCellContextMenu || sortDirections)
				? {
						...(headerActions ? { headerActions } : {}),
						...(sortDirections ? { sortDirections } : {}),
						...(wireCellContextMenu && primitiveLeafType !== undefined
							? {
									onCellContextMenu: (cell, e) =>
										options.onCellContextMenu?.(fieldPath, resolveCellPrimitiveValue(renderPlan, cell.row.original), primitiveLeafType, e),
								}
							: {}),
					}
				: undefined,
		});

		perCollection.set(cacheKey, descriptor);
		return descriptor;
	}

	const fieldColumns = computed(() => toValue(options.visibleColumnPaths).flatMap((fieldPath) => {
		const descriptor = getCachedFieldColumn(fieldPath);
		return descriptor ? [descriptor] : [];
	}));

	return { fieldColumns };
}
